import { Box, Flex, Skeleton, useToast } from '@chakra-ui/react';
import assert from 'assert';
import React from 'react';

import { utils as commonUtils } from '@finley-technologies/common';

import { useGetUserQuery } from 'types/dashboard-core-gqlTypes.generated.d';

import { useDataMetadataContext } from '../hooks/DataMetadataContext';
import { ValleyBankHeader } from './ValleyBankHeader';
import { ValleyBankSideBar } from './ValleyBankSideBar';
import { errorToastConfig } from 'packages/common-frontend/constants';
import CreditFacilityProvider from 'packages/credit-facility/hooks/CreditFacilityProvider';
import CreditFacilityMissingFallback from 'packages/credit-facility/ui/CreditFacilityMissingFallback';
import { HEADER_HEIGHT } from 'packages/header/ui/Header';
import { useUserRoleTypePermissionsContext } from 'valleyBank/domains/page/hooks/useUserRoleTypePermissions';
import { ValleyBankDataMedataHeader } from 'valleyBank/domains/page/ui/ValleyBankDataMedataHeader';

type ValleyBankTemplateProps = {
  canToggleDataSource?: boolean;
  children: React.ReactNode;
};
export const ValleyBankTemplate = ({
  children,
  canToggleDataSource = false,
}: ValleyBankTemplateProps) => {
  const toast = useToast();
  const { permissions } = useUserRoleTypePermissionsContext();
  const context = useDataMetadataContext();
  const { dashboardMode } = context;
  const isAssignmentEnabled =
    dashboardMode === 'current' && permissions.assignmentQueueRead;
  const isAdvanceRateEnabled =
    dashboardMode !== 'historical' && permissions.assignmentQueueRead;
  const { data, loading } = useGetUserQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = commonUtils.EnvUtils.isProduction()
        ? 'An unexpected error occurred. Please contact Finley for support.'
        : err.message;
      toast(errorToastConfig);
    },
  });
  if (!data || loading) {
    return (
      <Skeleton data-testid="loading-capital-provider-router" height="600px" />
    );
  }

  const { user } = data;
  assert(user, 'User expected to exist');

  return (
    <>
      <ValleyBankHeader />
      {canToggleDataSource && <ValleyBankDataMedataHeader context={context} />}
      <Flex
        minH="95vh"
        grow={1}
        pt={`${canToggleDataSource ? HEADER_HEIGHT * 2 : HEADER_HEIGHT}px`}
      >
        <CreditFacilityProvider
          organizationId={user.organizationId}
          renderLoading={<Skeleton />}
          fallbackComponent={<CreditFacilityMissingFallback />}
        >
          <ValleyBankSideBar
            canToggleDataSource={canToggleDataSource}
            isAssignmentEnabled={isAssignmentEnabled}
            isAdvanceRateEnabled={isAdvanceRateEnabled}
          />
          <Box width="100%" overflowX="auto">
            {children}
          </Box>
        </CreditFacilityProvider>
      </Flex>
    </>
  );
};
